"use client";
//import react
import React, { useCallback, useEffect, useMemo, useState } from "react";
import Image from "next/image";
// External Lib
import { Box } from "@mui/material";
// Internal Lib
import { showToast } from "@int-traderjoes/ui-components-library";
// Contexts
import { useTheme } from "@/context";
import { useFormContext } from "@/context/FormContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { useLibraryComponents } from "@/hooks/useLibraryComponents";
import { useGlobalStateContext } from "@/context/GlobalContext";
//Custom components
import CustomTooltip from "../Tooltip";
import NewerVersionDocumentsBanner from "@/components/Common/NewerVersionDocumentsBanner";
// services
import AuthService from "@/services/authService";
import FileService from "@/services/fileService";
import PimcoreService from "@/services/pimcoreService";
// Utils
import { RequiredDocumentsValidateFile, getFileCategory } from "@/utils/helper";
import { FILE_VALIDATION_CONFIG } from "@/utils/fileValidationConstants";
import { handleApiError } from "@/hooks/useApiErrorHandler";

import { BaseRequiredDocsProps, RequiredDocsHeader, RequiredDocsLabel } from "../RequiredDocumentsForm";
//Assets
import helpIcon from "@/assets/images/ideation-phase-images/HelpIcon.svg";
// Styles
import "./requiredDocumentsTJUser.scss";
import { useFileManagement } from "@/hooks";
import ConfirmationBox from "@/components/ConfirmationBox";

interface RequiredDocumentsTJUserFormProps extends BaseRequiredDocsProps {
  initialNdaFileName: string;
  isFormDisabled?: boolean;
  formUpdated: () => void;
  vendorId?: string;
  /** When true (e.g. vendor champion / existing vendor flow), show NDA newer-version banner if an NDA is already present */
  hasExistingVendor?: boolean | null;
  /**
   * When set (e.g. vendor champion after `getAllVendorAssessment`), the NDA banner requires this
   * to be true (NDA returned by the assessment API) in addition to NDA data in form context.
   * Omitted keeps prior behavior (banner follows form state only).
   */
  ndaPresentFromAssessmentApi?: boolean;
  onNDAStateChange?: (state: {
    isNdaDeleted: boolean;
    ndaReUpload: { id?: string; filename?: string; type?: string } | null;
  }) => void;
  resetReUploadRef?: React.MutableRefObject<(() => void) | null>;
  /**
   * When false, hides the secondary "re-upload signed NDA" control.
   * Vendor Assessment keeps the default (true); Vendor Champion Form passes false.
   */
  showNdaReupload?: boolean;
}

const RequiredDocumentsForm: React.FC<RequiredDocumentsTJUserFormProps> = ({
  isExpanded,
  onToggle,
  isVendor,
  initialNdaFileName,
  isFormDisabled,
  formUpdated,
  vendorId,
  hasExistingVendor: hasExistingVendorFromApi,
  ndaPresentFromAssessmentApi,
  onNDAStateChange,
  resetReUploadRef,
  showNdaReupload = true
}) => {
  const { darkMode } = useTheme();
  const displayMode = () => {
    return darkMode? "dark":"light";
  };
  const darkModeClass = darkMode ? "dark" : "light";

  const { FileUploadDisplay } = useLibraryComponents();
  const { setIsLoading } = useGlobalStateContext();

  const [isFileSelected, setIsFileSelected] = useState<boolean>(false);
  const [fileSelected, setFileSelected] = useState<File | null>(null);
  const [fileInputKey, setFileInputKey] = useState<number>(0);
  
  // State to track if NDA file was deleted (for passing nda: null on save)
  const [isNdaDeleted, setIsNdaDeleted] = useState<boolean>(false);
  
  // Store original NDA separately for display (left side)
  const [originalNda, setOriginalNda] = useState<{ id?: string; filename?: string; type?: string } | null>(null);
  
  // Store re-upload file separately for display (right side) and payload
  const [ndaReUpload, setNdaReUpload] = useState<{ id?: string; filename?: string; type?: string } | null>(null);
  const [isNdaReUploading, setIsNdaReUploading] = useState<boolean>(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteTarget, setPendingDeleteTarget] = useState<"main" | "reupload" | null>(null);

  const { setRequiredDocuments, isVendorAssessmentSubmitClicked, requiredDocuments } = useFormContext();
  const { languageText } = useLanguageContext();
  const requiredDocumentsLabels = languageText?.LABELS?.VENDOR_INFORMATION?.REQUIRED_DOCUMENTS_FORM;
  const requiredDocumentsInfoIconMessages = languageText?.MESSAGES?.INFO_ICON_MESSAGES?.VENDOR_INFORMATION?.REQUIRED_DOCUMENTS_FORM;
  const formHeader = languageText?.TABLE_AND_FORM_HEADER?.FORM_HEADER?.REQUIRED_DOCUMENTS;
  const toastMessages = languageText?.MESSAGES?.TOAST_MESSAGES;
  const buttonTexts = languageText?.BUTTONS;
  const confirmationBoxMessages = languageText?.MESSAGES?.CONFIRMATION_BOX_MESSAGES;
  const deleteDocumentConfirmTitle =
    confirmationBoxMessages?.CONFIRM_TITLE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_TITLE ||
    "Delete Document";
  const deleteDocumentConfirmMessage =
    confirmationBoxMessages?.CONFIRM_MESSAGE?.VENDOR_LIBRARY_DOCUMENT_DELETE_CONFIRM_MSG ||
    "Are you sure you want to delete?";
  
  // Top-level constant for validation messages
  const validationMessages = languageText?.MESSAGES?.VALIDATION_MESSAGES;

  const isExistingVendor = hasExistingVendorFromApi ?? false;
  const [showNdaNewerVersionBanner, setShowNdaNewerVersionBanner] =
    useState<boolean>(true);

  /** Same as RequiredDocumentsForm: NDA already present from API or legacy fields */
  const hasNdaDataInField = useMemo(() => {
    if (requiredDocuments == null) return false;
    const idOrFileId = requiredDocuments.id ?? requiredDocuments.fileId;
    if (idOrFileId != null && String(idOrFileId).trim() !== "") return true;
    const fn = requiredDocuments.filename;
    if (typeof fn === "string" && fn.trim() !== "") return true;
    const data = requiredDocuments.data;
    if (typeof data === "string" && data.trim() !== "") return true;
    return false;
  }, [requiredDocuments]);

  const ndaBannerAdditionalCondition =
    ndaPresentFromAssessmentApi === undefined
      ? hasNdaDataInField
      : Boolean(ndaPresentFromAssessmentApi) && hasNdaDataInField;

  // File validation configuration for NDA (PDF only)
  const { allowedTypes: ndaAllowedTypes, toastMessage: ndaToastMessage } = FILE_VALIDATION_CONFIG.NDA_DOCUMENTS;

  const documentFields = useMemo(() => ['ndaDocument'], []);

  const {
    uploadFile,
    downloadFile,
    removeFile,
    getAllFilesForPayload,
    isUploading,
    hasFile,
    getFileName,
    populateExistingFiles,
    clearAllFiles
  } = useFileManagement({
    productId: vendorId || 'unknown',
    variantId: 'nda',
    documentFields,
    onFileStateChange: useCallback((fieldName: string, fileData: any) => {
      console.log(`🔄 [RequiredDocumentsTJUser] NDA file state changed:`, fileData);
      
      // Update required documents with asset ID
      if (fileData && fileData.id) {
        setRequiredDocuments({
            id: fileData.id,
            filename: fileData.filename,
            type: getFileCategory('application/pdf'), // Default type
            data: "", // No base64 data with asset API
        });
        formUpdated();
      }
    }, [setRequiredDocuments, formUpdated]),
    uploadOptions: useMemo(() => ({
      maxSize: 40 * 1024 * 1024, // 40MB
      allowedTypes: ['application/pdf'],
      allowedExtensions: ['pdf'],
      isGuest: isVendor // Use guest API for vendor context
    }), [isVendor]),
    isGuest: isVendor // Use guest API for vendor context
  });

  const requiredDocumentsFormPermissions = useMemo(() => 
    AuthService.fetchPermissions("requiredDocuments"), 
    []
  );

  // Store original NDA when component loads or when main NDA is uploaded/updated
  useEffect(() => {
    if (requiredDocuments?.id) {
      // Update originalNda whenever requiredDocuments.id changes (including after save)
      setOriginalNda({
        id: requiredDocuments.id,
        filename: requiredDocuments.filename,
        type: requiredDocuments.type
      });
    }
    else {
      setOriginalNda(null);
    }
  }, [requiredDocuments?.id, requiredDocuments?.filename, requiredDocuments?.type]);

  // Reset deletion flag when new files are uploaded
  useEffect(() => {
    if (requiredDocuments?.id || ndaReUpload?.id) {
      if (isNdaDeleted) {
        setIsNdaDeleted(false);
      }
    }
  }, [requiredDocuments?.id, ndaReUpload?.id, isNdaDeleted]);

  // Expose state to parent component for payload creation
  useEffect(() => {
    if (onNDAStateChange) {
      onNDAStateChange({
        isNdaDeleted,
        ndaReUpload // Expose re-upload file for payload
      });
    }
  }, [isNdaDeleted, ndaReUpload, onNDAStateChange]);

  // Expose reset function to parent component
  useEffect(() => {
    if (resetReUploadRef) {
      resetReUploadRef.current = () => {
        setNdaReUpload(null);
      };
    }
  }, [resetReUploadRef]);

  useEffect(() => {
    if (!showNdaReupload) {
      setNdaReUpload(null);
      setIsNdaReUploading(false);
    }
  }, [showNdaReupload]);

  // Unified file upload handler for both main NDA and re-upload - similar to RequiredDocumentsForm and MVADocumentReview
  const handleFileChange = useCallback(async (file: File | null, isReUploadField: boolean = false) => {
    // Respect disabled state
    if (isFieldDisabled("nda") || !requiredDocumentsFormPermissions.nda?.isEditable) {
      return;
    }
    
    try {
      if (file === null) {
        // Delete flow
        if (isReUploadField) {
          // Delete re-upload file only
          console.log(`🗑️ [RequiredDocumentsTJUser] Removing re-upload file`);
          setNdaReUpload(null);
          setIsNdaReUploading(false);
          setFileSelected(null);
          setIsFileSelected(false);
          setFileInputKey(prev => prev + 1);
          formUpdated();
          return;
        } else {
          // Delete main NDA file
          console.log(`🗑️ [RequiredDocumentsTJUser] Removing NDA file`);
          const fieldName = 'ndaDocument';
          // await removeFile(fieldName);
          
          // Mark as deleted for save payload
          setIsNdaDeleted(true);

          // Clear form data and original NDA
          setRequiredDocuments({
            filename: "",
            type: "",
            data: "",
            id: ""
          });
          setOriginalNda(null);
          setNdaReUpload(null); // Also clear re-upload if main NDA is deleted
          
          // Update UI state
          setFileSelected(null);
          setFileInputKey(prevKey => prevKey + 1);
          formUpdated();
          return;
        }
      }

      // Upload flow
      if (!file) return;
      if (!RequiredDocumentsValidateFile(file, validationMessages)) {
        setFileSelected(null);
        setFileInputKey((prevKey) => prevKey + 1);
        return;
      }

      setIsFileSelected(true);
      setFileSelected(file);

      try {
        setIsLoading(true);

        if (isReUploadField) {
          // Re-upload flow: Use FileService directly and store separately (don't replace original NDA)
          console.log(`🔄 [RequiredDocumentsTJUser] Uploading NDA re-upload file:`, file.name);
          setIsNdaReUploading(true);
          const uploadResponse = await FileService.uploadFile(file, {
            allowedTypes: ['application/pdf'],
            allowedExtensions: ['pdf']
          });

          // Store re-upload file separately (for display on right side and payload)
          setNdaReUpload({
            id: uploadResponse.id,
            filename: uploadResponse.filename || file.name,
            type: getFileCategory(file.type),
          });
          
          // Keep original NDA intact (left side display)
          // Don't update requiredDocuments - keep original NDA there for display
          
          // Reset deletion flag if file was previously deleted
          if (isNdaDeleted) {
            setIsNdaDeleted(false);
          }

          console.log(`✅ [RequiredDocumentsTJUser] NDA re-upload file uploaded successfully. Re-upload ID: ${uploadResponse.id}`);
        } else {
          // Main NDA upload flow: Use useFileManagement hook (similar to RequiredDocumentsForm)
          console.log(`🔄 [RequiredDocumentsTJUser] Uploading NDA file:`, file.name);
          
          const fieldName = 'ndaDocument';
          const customPath = `vendor-assessment/${vendorId || 'unknown'}/nda/${file.name}`;
          await uploadFile(fieldName, file, customPath);
          
          // Note: requiredDocuments will be updated via onFileStateChange callback
          // originalNda will be set in the useEffect that watches requiredDocuments.id
          
          // Clear re-upload if main NDA is being replaced
          setNdaReUpload(null);
          
          // Reset deletion flag if file was previously deleted
          if (isNdaDeleted) {
            setIsNdaDeleted(false);
          }

          console.log(`✅ [RequiredDocumentsTJUser] NDA file uploaded successfully`);
        }

      } finally {
        // Always hide loader, even if upload fails
        setIsLoading(false);
        setIsNdaReUploading(false);
      }

      // Clear file selection state after successful upload
      setIsFileSelected(false);
      setFileSelected(null);
      setFileInputKey((prevKey) => prevKey + 1);

      // Only show toast for re-upload - useFileManagement already shows toast for main NDA upload
      if (isReUploadField) {
        showToast({
          type: "success",
          message: toastMessages?.FILE_UPLOAD_SUCCESS?.replace('{fileName}', file.name) || `${file.name} uploaded successfully.`,
        });
      }

      formUpdated();

    } catch (error) {
      console.error(`❌ [RequiredDocumentsTJUser] Error handling NDA file:`, error);
      setIsFileSelected(false);
      setFileSelected(null);
      setFileInputKey(prevKey => prevKey + 1);
      // Toast is already shown by useFileManagement hook — no duplicate error toast here.
    }
  }, [
    uploadFile, 
    removeFile, 
    vendorId, 
    setRequiredDocuments, 
    formUpdated, 
    setIsLoading, 
    isFormDisabled, 
    requiredDocumentsFormPermissions, 
    isNdaDeleted,
    validationMessages,
    toastMessages,
    populateExistingFiles
  ]);


  // Load existing NDA file if asset ID exists
  useEffect(() => {
    const loadExistingNDAFile = async () => {
      try {
        const existingNda = requiredDocuments;
        
        if (existingNda?.id && existingNda?.filename) {
          console.log('📁 [RequiredDocumentsTJUser] Loading existing NDA file:', existingNda);
          
          // Populate the file management with existing asset ID
          const fileMap = {
            ndaDocument: {
              id: existingNda.id,
              filename: existingNda.filename
            }
          };
          
          populateExistingFiles(fileMap);
        }
      } catch (error) {
        console.error('❌ [RequiredDocumentsTJUser] Error loading existing NDA file:', error);
      }
    };

    if (requiredDocuments?.id) {
      loadExistingNDAFile();
    }
    else {
      clearAllFiles();
    }
  }, [requiredDocuments?.id, requiredDocuments?.filename, populateExistingFiles]);

  // Delete handler for main NDA file
  const handleDeleteFile = useCallback(async () => {
    await handleFileChange(null, false);
  }, [handleFileChange]);

  const handleRequestDeleteFile = useCallback((target: "main" | "reupload") => {
    setPendingDeleteTarget(target);
    setDeleteConfirmOpen(true);
  }, []);

  const handleCancelDeleteFile = useCallback(() => {
    setDeleteConfirmOpen(false);
    setPendingDeleteTarget(null);
  }, []);

  const handleConfirmDeleteFile = useCallback(() => {
    const target = pendingDeleteTarget;
    setDeleteConfirmOpen(false);
    setPendingDeleteTarget(null);
    if (target === "main") {
      void handleDeleteFile();
      showToast({
        type: "success",
        message: validationMessages?.FILE_REMOVED_SUCCESS || "File removed successfully",
      });
    } else if (target === "reupload") {
      void handleFileChange(null, true);
      showToast({
        type: "success",
        message: validationMessages?.FILE_REMOVED_SUCCESS || "File removed successfully",
      });
    }
  }, [pendingDeleteTarget, handleDeleteFile, handleFileChange, validationMessages?.FILE_REMOVED_SUCCESS]);


  const handleDownloadTemplateOnly = useCallback(async () => {
    try {
      setIsLoading(true);
      const blob = await PimcoreService.getNDATemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = "Trader-Joe's-Non-Disclosure-Agreement.pdf";
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      handleApiError(
        error,
        languageText?.MESSAGES?.TOAST_MESSAGES?.FILE_VALIDATION?.NDA_TEMPLATE_DOWNLOAD_ERROR ||
          "Failed to download NDA template."
      );
    } finally {
      setIsLoading(false);
    }
  }, [languageText, setIsLoading]);

  // Download handler for NDA file
  const handleDownloadNda = useCallback(async () => {
    if (requiredDocuments?.fullpath) {
      try {
        window.open(requiredDocuments.fullpath, "_blank");
      } catch (error) {
        console.error("Error downloading document:", error);
        handleApiError(
          error,
          validationMessages?.DOWNLOAD_FAILED || "Failed to download document"
        );
      }
    }
  }, [requiredDocuments?.fullpath, validationMessages]);

  const handleDownloadFile = useCallback(async () => {
    const fieldName = 'ndaDocument';
    try {
      await downloadFile(fieldName);
    } catch (error) {
      console.error("Failed to download NDA file:", error);
      handleApiError(
        error,
        validationMessages?.FAILED_DOWNLOAD_ATTACHMENT_ERROR || "Failed to download file."
      );
    }
  }, [downloadFile]);

  useEffect(() => {
    if (requiredDocuments?.filename || ndaReUpload?.filename) {
      setIsFileSelected(true);
    } else {
      setIsFileSelected(false);
    }
  }, [requiredDocuments, ndaReUpload]);

  const isFieldDisabled = (fieldName: string) => {
     if (isFormDisabled) return true;
     return false;
   };

  const fieldName = 'ndaDocument';
  const hasUploadedFile = hasFile(fieldName);
  const fileName = getFileName(fieldName);
  const isUploadingFile = isUploading(fieldName);
  const ndaFieldHasError =
    !originalNda?.id && !ndaReUpload?.id && isVendorAssessmentSubmitClicked;

  return (
    <Box
      className={`form-container ${isExpanded ? "form-container-expanded" : ""
        }`}
    >
      <RequiredDocsHeader
        isExpanded={isExpanded}
        formHeader={formHeader}
        onToggle={onToggle}
        theme={displayMode()}
       />
      <form onSubmit={(e) => e.preventDefault()}>
        {isExpanded && (
           <>
            <NewerVersionDocumentsBanner
              isExistingVendor={isExistingVendor}
              additionalCondition={ndaBannerAdditionalCondition}
              showBanner={showNdaNewerVersionBanner}
              onClose={() => setShowNdaNewerVersionBanner(false)}
              className="nda-banner-container"
              wrapperSx={{ px: "1.5rem" }}
            />
          <Box className="field-group">

            {/* NDA */}
            <Box>
              <RequiredDocsLabel
                theme={darkMode ? "dark" : "light"}
                label={requiredDocumentsLabels?.NDA}
                isError={ndaFieldHasError}
                required={requiredDocumentsFormPermissions?.nda?.isMandatory ?? false}
                toolTipMessage={requiredDocumentsInfoIconMessages?.CP_VA_RDF_UPLOAD_NDA_INFO}
                onToolTipLinkClick={handleDownloadTemplateOnly}
               />
              <FileUploadDisplay
                hasUploadedFile={!isNdaDeleted && Boolean(originalNda?.id || requiredDocuments?.id)}
                fileName={originalNda?.filename || fileName || initialNdaFileName || ""}
                isUploading={isUploadingFile}
                uploadComplete={!isUploadingFile && Boolean(originalNda?.id || requiredDocuments?.id)}
                onFileChange={(file) => handleFileChange(file, false)}
                placeholder="UPLOAD NON-DISCLOSURE AGREEMENT DOCUMENT"
                fileInputValue={fileSelected || null}
                fileInputKey={`nda-file-input-${fileInputKey}`}
                allowedTypes={[...ndaAllowedTypes]}
                toastMessage={ndaToastMessage}
                onDownload={handleDownloadFile}
                onDelete={() => handleRequestDeleteFile("main")}
                darkModeClass={darkModeClass}
                hideDownloadButton={!Boolean(originalNda?.id || requiredDocuments?.id || requiredDocuments?.fullpath)}
                htmlFor="nda-document"
                required={requiredDocumentsFormPermissions?.nda?.isMandatory ?? false}
                disabled={!(requiredDocumentsFormPermissions?.nda?.isEditable ?? false) || isFieldDisabled("nda")}
                error={ndaFieldHasError}
              />
              {/* Template download link */}
              <span 
                className="download-file-text" 
                style={{ cursor: 'pointer' }}
                onClick={handleDownloadTemplateOnly}
              >
                [{languageText?.BUTTONS?.DOWNLOAD_FILE_TEMPLATE}]
              </span>
            </Box>
            {/* Show re-upload box if vendor's NDA file exists (Vendor Assessment); hidden on Vendor Champion Form */}
            {showNdaReupload &&
              (originalNda?.id || requiredDocuments?.id || requiredDocuments?.fullpath) && (
              <Box className="re-upload-document">
                <FileUploadDisplay
                  hasUploadedFile={!isNdaReUploading && Boolean(ndaReUpload?.id)}
                  fileName={ndaReUpload?.filename || ""}
                  isUploading={isNdaReUploading}
                  uploadComplete={!isNdaReUploading && Boolean(ndaReUpload?.id)}
                  onFileChange={(file) => handleFileChange(file, true)}
                  placeholder="RE-UPLOAD NON-DISCLOSURE AGREEMENT SIGNED FILE"
                  fileInputValue={fileSelected || null}
                  fileInputKey={`nda-reupload-input-${fileInputKey}`}
                  allowedTypes={[...ndaAllowedTypes]}
                  toastMessage={ndaToastMessage}
                  onDownload={() => {}} // No download action for re-upload
                  onDelete={() => handleRequestDeleteFile("reupload")}
                  darkModeClass={darkModeClass}
                  htmlFor="nda-reupload-document"
                  required={false}
                  disabled={!requiredDocumentsFormPermissions.nda?.isEditable || isFieldDisabled("nda")}
                />
              </Box>
            )}
            
      </Box>
      
    </>
    )}
  </form >
  <ConfirmationBox
    open={deleteConfirmOpen}
    onClose={handleCancelDeleteFile}
    onDecline={handleCancelDeleteFile}
    onConfirm={handleConfirmDeleteFile}
    title={deleteDocumentConfirmTitle}
    message={deleteDocumentConfirmMessage}
    confirmText={buttonTexts?.DELETE || "Delete"}
    cancelText={buttonTexts?.CANCEL_BUTTON || "Cancel"}
    showCancelButton
  />
</Box >
);
};
export default RequiredDocumentsForm;